<template>
  <Component
    :is="type"
    v-if="version === VersionOptions.LEGACY || dsComponentDetails.version === VersionOptions.LEGACY"
    :class="['btn', size, state, variation]"
    v-bind="$attrs"
    v-on="$attrs"
  >
    <!--
      @slot text, components, or HtmlElements as children of the component
     -->
    <slot>
      <sb-text value="common.submit" />
    </slot>
  </Component>
  <SbButtonNext
    v-else
    :type="type"
    :size="size"
    :state="state"
    :variation="variation"
    :icon="icon"
    :data-sb-automation="$attrs['data-sb-automation'] || 'sb-button'"
    :label="label"
  >
    <!--
      @slot text, components, or HtmlElements as children of the component
     -->
    <slot />
  </SbButtonNext>
</template>

<script>
import SbText from '@src/components/Text/SbText.vue';
import SbButtonNext from '@src/components/Button/SbButtonNext.vue';
import { versionedComponentsMixin } from '@src/mixins/versionedComponents';

/**
 * Buttons are generally used for interface actions. Suitable for all-purpose use.
 * This is an extension to the standard button element with icons and theming.
 */
export default {
  name: 'SbButton',
  components: {
    SbText,
    SbButtonNext,
  },
  mixins: [versionedComponentsMixin],
  props: {
    /**
     * The `label` prop controls the text displayed by the button.
     */
    label: {
      type: String,
      default: null,
    },
    /**
     * The html element used for the button.
     */
    type: {
      type: String,
      default: 'button',
      validator: (value) => {
        return value.match(/(button|a)/);
      },
    },
    /**
     * The size of the button. Defaults to medium.
     * `extra-small, small, medium, large`
     */
    size: {
      type: String,
      default: 'medium',
      validator: (value) => {
        return value.match(/(extra-small|small|medium|large)/);
      },
    },
    /**
     * Manually trigger various states of the button.
     * `hover, active, focus`
     *
     * @deprecated CSS should handle these states automatically. This prop will be removed in a future version.
     */
    state: {
      type: String,
      default: null,
      validator: (value) => {
        return value.match(/(hover|active|focus)/);
      },
    },
    /**
     * Style variation to give additional meaning.
     *  `primary, secondary, tertiary, warning, minimal, caution, link, iconOnly`
     *
     * @values primary secondary tertiary warning minimal caution link iconOnly
     */
    variation: {
      type: String,
      default: 'primary',
      validator: (value) => value.match(/(primary|secondary|tertiary|warning|minimal|caution|link|iconOnly)/),
    },
    /**
     * The icon to be displayed in the button.
     * Can be provided in several ways:
     * - CSS class name for an icon font (e.g., "pi pi-plus").
     * - Raw SVG string.
     * - Path to an SVG asset (if your build process handles this).
     */
    icon: {
      type: String,
      default: null,
    },
    /**
     * If true, the button will take the full width of its container.
     * This is useful for buttons that need to be responsive or fill available space.
     */
    fluid: {
      type: Boolean,
      default: false,
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@src/components/Button/style/sb-button.scss';
</style>
